import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { LoadingBlock, Spinner } from '@/components/Spinner';
import '@/styles/pages.css';

/**
 * Sign in.
 *
 * Up to three routes, and the server says which are on: Microsoft 365 single
 * sign-on, a password, or a code emailed to a company address. The code route
 * is not a fallback — it is how an account that has never had a password gets
 * in the first time, and it stays the recovery path when SSO is unavailable.
 *
 * Microsoft is drawn first and alone when it is enabled, because once the
 * company is on it that is the button everyone should press; the other two
 * sit under a divider so they are there without competing.
 *
 * The prototype's "quick login" list of UAT accounts is deliberately gone:
 * publishing real employee addresses on a login page is a disclosure problem,
 * not a convenience.
 */
type Mode = 'password' | 'code';

export function LoginPage(): ReactNode {
  const {
    signIn,
    requestCode,
    signInWithCode,
    signInWithMicrosoft,
    methods,
    loading,
    error,
    clearError,
  } = useAuth();

  const [mode, setMode] = useState<Mode>('password');
  const [leaving, setLeaving] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const trimmedEmail = email.trim().toLowerCase();

  async function onSubmitPassword(e: FormEvent): Promise<void> {
    e.preventDefault();
    clearError();
    try {
      await signIn(trimmedEmail, password);
    } catch {
      // The provider already holds the message; nothing to add here.
    }
  }

  async function onRequestCode(e: FormEvent): Promise<void> {
    e.preventDefault();
    clearError();
    setSending(true);
    try {
      await requestCode(trimmedEmail);
      setCodeSent(true);
      // Deliberately the same wording whether or not the address has an
      // account, so this page cannot be used to discover who works here.
      setNotice(`If ${trimmedEmail} has an account, a six-digit code is on its way.`);
    } catch {
      /* handled by the provider */
    } finally {
      setSending(false);
    }
  }

  async function onSubmitCode(e: FormEvent): Promise<void> {
    e.preventDefault();
    clearError();
    try {
      await signInWithCode(trimmedEmail, code.trim());
    } catch {
      /* handled by the provider */
    }
  }

  function switchMode(next: Mode): void {
    setMode(next);
    setNotice(null);
    setCodeSent(false);
    setCode('');
    clearError();
  }

  async function onMicrosoft(): Promise<void> {
    clearError();
    setLeaving(true);
    try {
      // Come back to the page that was asked for, as a password sign-in would.
      const here = `${window.location.pathname}${window.location.search}`;
      await signInWithMicrosoft(here === '/' ? undefined : here);
      // The browser is navigating away; nothing to do.
    } catch {
      setLeaving(false);
    }
  }

  const passwordOn = methods?.password ?? true;
  const emailCode = methods?.emailCode ?? true;
  const microsoft = methods?.microsoft ?? false;
  const local = passwordOn || emailCode;

  // If the mode the page opened on has been switched off, move to the other.
  useEffect(() => {
    if (mode === 'password' && !passwordOn && emailCode) setMode('code');
    if (mode === 'code' && !emailCode && passwordOn) setMode('password');
  }, [mode, passwordOn, emailCode]);

  if (!methods) {
    return <LoadingBlock label="Loading…" />;
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-side">
          <img className="auth-logo" src="/logo.svg" alt="Great Deals E-Commerce" />
          <h1>GDEC Expense Claim Management System</h1>
          <p>
            Submit receipts, follow your claim through approval and Finance, and see exactly which
            desk it is sitting on.
          </p>
          <p>Sign in with your GDEC company account.</p>
        </div>

        <div className="auth-form">
          <h2>Sign in</h2>
          <div className="sub">Company accounts only.</div>

          {error ? (
            <div className="form-error" role="alert">
              {error}
            </div>
          ) : null}

          {microsoft ? (
            <>
              <button
                type="button"
                className="btn btn-microsoft"
                onClick={() => void onMicrosoft()}
                disabled={leaving || loading}
              >
                {leaving ? <Spinner /> : <MicrosoftMark />} Sign in with Microsoft 365
              </button>
              <p className="hint">Use your GDEC Microsoft 365 account — the same one as Outlook and Teams.</p>
              {local ? <div className="auth-divider">or</div> : null}
            </>
          ) : null}

          {!microsoft && !local ? (
            <div className="form-notice">
              No sign-in method is currently enabled on this environment. Contact IT.
            </div>
          ) : null}

          {local && passwordOn && emailCode ? (
            <div className="mode-switch" role="tablist" aria-label="Sign-in method">
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'password'}
                className={mode === 'password' ? 'mode-tab active' : 'mode-tab'}
                onClick={() => switchMode('password')}
              >
                Password
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'code'}
                className={mode === 'code' ? 'mode-tab active' : 'mode-tab'}
                onClick={() => switchMode('code')}
              >
                Email me a code
              </button>
            </div>
          ) : null}

          {notice ? <div className="form-notice">{notice}</div> : null}

          {!local ? null : mode === 'password' && passwordOn ? (
            <form onSubmit={onSubmitPassword}>
              <div className="field">
                <label htmlFor="email">Company email</label>
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="field">
                <label htmlFor="password">Password</label>
                <div className="pw-wrap">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button type="button" onClick={() => setShowPassword((v) => !v)}>
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <button className="btn btn-primary" type="submit" disabled={loading}>
                {loading ? <Spinner /> : null} Sign in
              </button>

              {emailCode ? (
                <p className="hint">
                  No password yet? Use <b>Email me a code</b> — you can set a password once you are
                  in.
                </p>
              ) : null}
            </form>
          ) : !codeSent ? (
            <form onSubmit={onRequestCode}>
              <div className="field">
                <label htmlFor="otp-email">Company email</label>
                <input
                  id="otp-email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <button className="btn btn-primary" type="submit" disabled={sending}>
                {sending ? <Spinner /> : null} Send me a code
              </button>
            </form>
          ) : (
            <form onSubmit={onSubmitCode}>
              <div className="field">
                <label htmlFor="otp-code">Six-digit code</label>
                <input
                  id="otp-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />
                <p className="hint">It expires in ten minutes and can be used once.</p>
              </div>
              <button className="btn btn-primary" type="submit" disabled={loading}>
                {loading ? <Spinner /> : null} Sign in
              </button>
              <button
                type="button"
                className="link-btn"
                onClick={() => {
                  setCodeSent(false);
                  setNotice(null);
                }}
              >
                Use a different address
              </button>
            </form>
          )}

          <p className="hint auth-foot">
            Locked out? Contact the GDEC Help Desk — accounts are managed by your administrator.
          </p>
        </div>
      </div>
    </div>
  );
}

/** The four-square mark, inline so no request leaves the page for it. */
function MicrosoftMark(): ReactNode {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <rect x="0" y="0" width="7.5" height="7.5" fill="#f25022" />
      <rect x="8.5" y="0" width="7.5" height="7.5" fill="#7fba00" />
      <rect x="0" y="8.5" width="7.5" height="7.5" fill="#00a4ef" />
      <rect x="8.5" y="8.5" width="7.5" height="7.5" fill="#ffb900" />
    </svg>
  );
}
